import React, { useState, useMemo } from "react";
import Card, { CardHeader } from "components/card";
import Chart from "components/charts/Chart";
import Skeleton from "components/skeleton/Skeleton";
import { SegmentedControl } from "components/tabs";
import { palette } from "lib/chartTheme";
import { formatNumber } from "utils/format";
import CardError from "./CardError";

const TABS = [
  { value: "attendance", label: "Attendance", description: "Total weekly service attendance across all campuses" },
  { value: "firstTimeGuests", label: "Guests", description: "First-time visitors recorded at services" },
  { value: "salvations", label: "Salvations", description: "Salvation decisions made during services" },
  { value: "people", label: "People", description: "New members added to your Atrium database" },
];

const SERIES_COLORS = [palette.gold, palette.sage];

const BAR_OPTIONS_BASE = {
  colors: SERIES_COLORS,
  plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
};

const AREA_OPTIONS_BASE = {
  colors: SERIES_COLORS,
  fill: {
    type: "gradient",
    gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05, stops: [0, 100] },
  },
  stroke: { curve: "smooth", width: 2.5 },
  tooltip: { y: { formatter: (val) => `${val} members` } },
};

const PeopleGrowthChart = ({ data, loading, error, onRetry }) => {
  const [activeTab, setActiveTab] = useState("attendance");
  const isPeopleTab = activeTab === "people";
  const tab = TABS.find((t) => t.value === activeTab);

  const { series, options, statValue, statLabel } = useMemo(() => {
    const rows = data?.[activeTab];
    if (!rows?.length) return { series: [], options: undefined, statValue: null, statLabel: "" };

    const categories = rows.map((g) => g.month);
    const currentValues = rows.map((g) => g.current);
    const previousValues = rows.map((g) => g.previous);

    return {
      series: [
        { name: `${data.previousYear}`, data: previousValues },
        { name: `${data.currentYear}`, data: currentValues },
      ],
      options: {
        ...(isPeopleTab ? AREA_OPTIONS_BASE : BAR_OPTIONS_BASE),
        xaxis: { categories },
      },
      statValue: isPeopleTab
        ? formatNumber(data.total || 0)
        : formatNumber(currentValues.reduce((a, b) => a + b, 0)),
      statLabel: isPeopleTab ? "People in Atrium" : `${data.currentYear} YTD`,
    };
  }, [data, activeTab, isPeopleTab]);

  const renderStat = () => {
    if (loading) return <Skeleton className="h-10 w-24" />;
    if (statValue == null) return null;
    return (
      <div className="text-right">
        <p className="text-2xl font-bold leading-none text-fg">{statValue}</p>
        <p className="mt-1 text-xs text-fg-muted">{statLabel}</p>
      </div>
    );
  };

  return (
    <Card padding="md" className="gap-4">
      <CardHeader title="Growth Trends" description={tab?.description} actions={renderStat()} />
      <SegmentedControl
        aria-label="Growth metric"
        options={TABS}
        value={activeTab}
        onChange={setActiveTab}
        className="max-w-full self-start overflow-x-auto"
      />
      {error ? (
        <CardError title="Couldn't Load Growth Trends" error={error} onRetry={onRetry} />
      ) : (
        <Chart
          key={activeTab}
          type={isPeopleTab ? "area" : "bar"}
          series={series}
          options={options}
          height="sm"
          loading={loading}
          emptyText="No data yet"
        />
      )}
    </Card>
  );
};

export default PeopleGrowthChart;
